/**
 * "Looks sideways" detection for the Rotate PDF tool, from the page's own text layer (nothing is drawn or
 * recognised; scanned pages with no text layer simply get no suggestion). Runs on the device, on pdf.js pages.
 *
 * Each piece of text has a direction on the DISPLAYED page (the page's /Rotate is already part of pdf.js's
 * viewport). Upright text reads left to right. A page whose text mostly runs another way is flagged, with the
 * clockwise turn that would make it read upright. Pages with too little text, or with text running in mixed
 * directions, are left alone: a wrong suggestion is worse than none.
 */

export type QuarterTurn = 0 | 90 | 180 | 270;

export interface TurnSuggestion {
  page: number;
  /** Clockwise turn that makes the text read upright, as the Rotate tool's angle: 90, 180 or -90. */
  turn: 90 | 180 | -90;
  /** How many letters and digits were read, and the share of them running the same way. */
  chars: number;
  share: number;
}

/** Fewest letters and digits a page needs before its direction is trusted. */
export const MIN_CHARS = 25;
/** Share of the page's characters that must run the same (non-upright) way. */
export const MIN_SHARE = 0.75;
/** A text run further than this from a quarter turn counts as slanted and is ignored. */
const SLANT_TOLERANCE_DEG = 15;

type Mat = number[];

interface TextRun { str: string; transform: number[] }

/** Direction of each text run on the displayed page, weighted by its letters and digits. Pure; unit-tested. */
export function directionVotes(runs: TextRun[], viewportTransform: Mat): Record<QuarterTurn, number> {
  const votes: Record<QuarterTurn, number> = { 0: 0, 90: 0, 180: 0, 270: 0 };
  for (const run of runs) {
    if (typeof run.str !== "string" || !Array.isArray(run.transform)) continue;
    const weight = (run.str.match(/[^\s!-\/:-@[-`{-~]/g) ?? []).length;
    if (weight === 0) continue;
    const tr = run.transform;
    const dx = viewportTransform[0] * tr[0] + viewportTransform[2] * tr[1];
    const dy = viewportTransform[1] * tr[0] + viewportTransform[3] * tr[1];
    if (!dx && !dy) continue;
    const deg = (Math.atan2(dy, dx) * 180) / Math.PI; // screen angle, clockwise positive (y points down)
    const bucket = Math.round(deg / 90) * 90;
    if (Math.abs(deg - bucket) > SLANT_TOLERANCE_DEG) continue;
    votes[((((bucket % 360) + 360) % 360) as QuarterTurn)] += weight;
  }
  return votes;
}

/** The suggestion those votes add up to, or null when the page reads upright or the evidence is weak. */
export function suggestFromVotes(page: number, votes: Record<QuarterTurn, number>): TurnSuggestion | null {
  const chars = votes[0] + votes[90] + votes[180] + votes[270];
  if (chars < MIN_CHARS) return null;
  let best: QuarterTurn = 0;
  for (const k of [90, 180, 270] as const) if (votes[k] > votes[best]) best = k;
  if (best === 0) return null;
  const share = votes[best] / chars;
  if (share < MIN_SHARE) return null;
  // Text that runs `best` degrees clockwise on screen is brought upright by turning the page back by `best`.
  const turn = best === 90 ? -90 : best === 270 ? 90 : 180;
  return { page, turn, chars, share };
}

/** Reads every page of an open pdf.js document and returns the pages that look sideways or upside down. */
export async function detectSidewaysPages(pdf: { numPages: number; getPage(n: number): Promise<any> }, isCurrent: () => boolean = () => true): Promise<TurnSuggestion[]> {
  const found: TurnSuggestion[] = [];
  for (let n = 1; n <= pdf.numPages; n++) {
    if (!isCurrent()) return found;
    try {
      const page = await pdf.getPage(n);
      const vt = page.getViewport({ scale: 1 }).transform as Mat;
      const content = await page.getTextContent();
      const s = suggestFromVotes(n, directionVotes(content.items as TextRun[], vt));
      if (s) found.push(s);
      page.cleanup?.();
    } catch {
      /* a page that cannot be read gets no suggestion */
    }
  }
  return found;
}

/** The turns for a batch: every page, or only odd or even pages, by the same quarter turn. Page numbers start at 1. */
export function batchTurns(pageCount: number, turn: 90 | 180 | -90, pages: "all" | "odd" | "even"): Record<number, number> {
  const out: Record<number, number> = {};
  for (let p = 1; p <= pageCount; p++) {
    if (pages === "odd" && p % 2 === 0) continue;
    if (pages === "even" && p % 2 === 1) continue;
    out[p] = turn;
  }
  return out;
}
